import React, { useEffect, useState } from 'react';
import { Box, Heading, VStack, HStack, Text, Button, Badge, Spinner, useColorModeValue } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../config/firebase.ts';

const API_BASE: string = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';
async function api(path: string, init?: RequestInit) {
  const token = await auth.currentUser?.getIdToken?.();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init?.headers || {})
  } as any;
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
}

export default function SmartClashHub() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>({ created: [], attempts: [] });
  const navigate = useNavigate();

  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.300', 'gray.700');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const res = await api('/api/smartclash/my', { method: 'GET' });
        if (active) setData(res);
      } catch (e) {
        // ignore
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <VStack py={10}>
        <Spinner size="lg" color="purple.400" />
        <Text>Chargement de vos défis...</Text>
      </VStack>
    );
  }

  const pendings = (data.created || []).filter((c: any) => (c.attemptsCount || 0) < 2);
  const finished = (data.created || []).filter((c: any) => (c.attemptsCount || 0) >= 2);

  return (
    <Box maxW="5xl" mx="auto" py={8}>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between" align="center">
          <Heading size="lg" color="purple.400">⚔️ Mes défis SmartClash</Heading>
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            leftIcon={<span>🏠</span>}
            _hover={{ transform: 'translateY(-1px)' }}
          >
            Retour à l’accueil
          </Button>
        </HStack>

  <Box bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg" p={5}>
          <Heading size="md" mb={3}>⏳ En attente</Heading>
          <VStack align="stretch" spacing={3}>
            {pendings.length === 0 ? (
              <Text color="gray.500">Aucun défi en attente</Text>
            ) : pendings.map((c: any) => (
              <HStack key={c.id} justify="space-between" bg={cardBg} p={3} borderRadius="md" border="1px" borderColor={borderColor}>
                <HStack>
                  <Badge colorScheme="purple">{c.categoryName || c.categoryKey}</Badge>
                  <Text fontSize="sm">{new Date(c.createdAt).toLocaleString()}</Text>
                </HStack>
                <HStack>
                  <Badge>{(c.attemptsCount || 0)}/2</Badge>
                  <Button size="sm" colorScheme="purple" onClick={() => navigate(`/smartclash/${c.id}`)}>Ouvrir</Button>
                  <Button size="sm" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/smartclash/${c.id}`)}>Copier le lien</Button>
                </HStack>
              </HStack>
            ))}
          </VStack>
        </Box>

        <Box bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg" p={5}>
          <Heading size="md" mb={3}>🏁 Terminés</Heading>
          <VStack align="stretch" spacing={3}>
            {finished.length === 0 ? (
              <Text color="gray.500">Aucun défi terminé</Text>
            ) : finished.map((c: any) => (
              <HStack key={c.id} justify="space-between" bg={cardBg} p={3} borderRadius="md" border="1px" borderColor={borderColor}>
                <HStack>
                  <Badge colorScheme="green">{c.categoryName || c.categoryKey}</Badge>
                  <Text fontSize="sm">{new Date(c.createdAt).toLocaleString()}</Text>
                </HStack>
                <HStack>
                  <Badge>{(c.attemptsCount || 0)}/2</Badge>
                  <Button size="sm" colorScheme="purple" onClick={() => navigate(`/smartclash/${c.id}`)}>Voir</Button>
                </HStack>
              </HStack>
            ))}
          </VStack>
        </Box>

        <Box bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg" p={5}>
          <Heading size="md" mb={3}>🧪 Mes dernières tentatives</Heading>
          <VStack align="stretch" spacing={3}>
            {(data.attempts || []).length === 0 ? (
              <Text color="gray.500">Aucune tentative</Text>
            ) : (data.attempts || []).map((a: any) => (
              <HStack key={a.attemptId} justify="space-between" bg={cardBg} p={3} borderRadius="md" border="1px" borderColor={borderColor}>
                <HStack>
                  <Badge colorScheme={(a.totalAttempts || 0) >= 2 ? 'green' : 'purple'}>{a.categoryName || a.categoryKey}</Badge>
                  <Text fontSize="sm">{new Date(a.submittedAt).toLocaleString()}</Text>
                </HStack>
                <HStack>
                  <Text>{a.totalPoints} pts</Text>
                  <Button size="sm" onClick={() => navigate(`/smartclash/${a.challengeId}`)}>Ouvrir</Button>
                </HStack>
              </HStack>
            ))}
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
